// src/PopulationChart.jsx
import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMoreModule from 'highcharts/highcharts-more'; // For arearange support
import ExportingModule from 'highcharts/modules/exporting'; // For exporting (optional)

// Initialize Highcharts modules
const HighchartsMore = HighchartsMoreModule.default || HighchartsMoreModule; // Handle ES module default export
const Exporting = ExportingModule.default || ExportingModule; // Handle ES module default export

if (typeof Highcharts === 'object') {
    try {
        HighchartsMore(Highcharts); // Initialize arearange support
        Exporting(Highcharts); // Initialize exporting module
    } catch (error) {
        console.error('Error initializing Highcharts modules:', error);
    }
} else {
    console.error('Highcharts core library not loaded');
}

const HazardLossChart = ({ lossData, ref, type }) => {
    const [dataset, setDataset] = useState(null);
    const startYear = 1990;
    const endYear = 2020;
    const [year, setYear] = useState(endYear); // Default to endYear (2020)
    const chartRef = useRef(null);
    const nbr = 20;
    const [isPlaying, setIsPlaying] = useState(false); // Track play/pause state
    const [currentYear, setCurrentYear] = useState(startYear);

    useEffect(() => {
        if (lossData) {
            setDataset(lossData)
        }
    }, [lossData]);

    const getData = (year) => {
        if (!dataset) return [];
        const output = Object.entries(dataset)
            .map(([countryName, countryData]) => [countryName, Number(countryData[year])])
            .sort((a, b) => b[1] - a[1]);
        return [output[0] || ['No Data', 0], output.slice(1, nbr)]; // Handle case where output[0] might be undefined
    };

    const getSubtitle = () => {
        const data = getData(year);
        if (!data || !data[1]) return ''; // Return an empty string if data is not valid

        const totalCount = data[1].reduce((sum, country) => sum + country[1], 0); // Sum all country values

        return `<span style="font-size: 30px;color:#000;font-weight:32px;font-family:Poppins">${year}</span>
            <br>
            <span style="color: #000;font-family: Poppins;font-size: 12px;font-style: normal;font-weight: 400;line-height: normal;">
                Total: <b>: ${type == 'death' ? totalCount : totalCount + ' USD'}</b>
            </span>`;
    };

    const chartOptions = {
        chart: {
            animation: false,
            marginRight: 50
        },
        title: {
            text: '',
            align: 'left'
        },
        exporting: {
            enabled: true
        },
        credits: {
            enabled: false
        },
        subtitle: {
            useHTML: true,
            text: getSubtitle(),
            floating: true,
            align: 'right',
            verticalAlign: 'middle',
            y: 150,
            x: -100
        },
        legend: {
            enabled: false
        },
        xAxis: {
            type: 'category'
        },
        yAxis: {
            opposite: true,
            tickPixelInterval: 150,
            title: {
                text: null
            }
        },
        plotOptions: {
            series: {
                animation: false,
                groupPadding: 0,
                pointPadding: 0.1,
                borderWidth: 0,
                colorByPoint: true,
                dataSorting: {
                    enabled: true,
                    matchByName: true
                },
                type: 'bar',
                dataLabels: {
                    enabled: true
                }
            }
        },
        series: [
            {
                type: 'bar',
                name: year,
                data: getData(year)[1]
            }
        ],
        exporting: {
            enabled: false,
        },
    };

    const play = () => {
        setYear(currentYear > startYear && currentYear < endYear ? currentYear : startYear); // Reset to startYear when play is clicked
        setIsPlaying(true); // Set playing state
        if (chartRef.current) {
            chartRef.current.chart.sequenceTimer = setInterval(() => {
                setYear((prevYear) => {
                    const newYear = prevYear + 1;
                    if (newYear > endYear) {
                        pause();
                        return endYear;
                    }
                    updateChart(newYear); // Update chart and slider
                    return newYear;
                });
            }, 1500); // 1 second interval
        }
    };

    useEffect(() => {
        setCurrentYear(year);
    }, [year])

    const updateChart = (newYear) => {
        if (chartRef.current) {
            chartRef.current.chart.update({
                subtitle: { text: getSubtitle(newYear) }, // Pass newYear
            });

            chartRef.current.chart.series[0].update({
                name: newYear,
                data: getData(newYear)[1],
            });
        }
    };

    const handleRangeChange = (event) => {
        const newYear = parseInt(event.target.value, 10);
        setCurrentYear(newYear);
        setYear(newYear);
        updateChart(newYear); // Sync chart with slider
    };

    const pause = () => {
        if (chartRef.current?.chart.sequenceTimer) {
            clearInterval(chartRef.current.chart.sequenceTimer);
            chartRef.current.chart.sequenceTimer = undefined;
        }
        setIsPlaying(false); // Set playing state
    };

    const handleButtonClick = () => {
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    };

    const downloadSVG = () => {
        if (chartRef.current) {
            const chart = chartRef.current.chart;
            const svg = chart.getSVG(); // Get SVG string
            const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "chart.svg";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };
    const downloadPNG = () => {
        if (chartRef.current) {
            const chart = chartRef.current.chart;
            const svg = chart.getSVG(); // Get SVG string
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();
            const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(svgBlob);

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                URL.revokeObjectURL(url);

                // Convert canvas to PNG and trigger download
                const link = document.createElement("a");
                link.href = canvas.toDataURL("image/png");
                link.download = "chart.png";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            };

            img.src = url;
        }
    };

    // Function to download chart as PNG
    // const downloadPNG = () => {
    //     if (chartRef.current) {
    //         chartRef.current.chart.exportChart({ type: 'image/png', filename: 'hazard-loss-chart' });
    //     }
    // };

    // Expose functions to parent
    useImperativeHandle(ref, () => ({
        downloadPNG,
    }));

    return (
        <div>
            {/* <div>
                <button className="btn btn-primary" onClick={downloadSVG}>Download SVG</button>
                <button className="btn btn-success" onClick={downloadPNG}>Download PNG</button>
            </div> */}
            <div>
                <button className="btn playBtn me-2" onClick={handleButtonClick}>
                    {isPlaying ? (
                        <i className="fas fa-circle-pause text-white"></i> // New Pause Icon
                    ) : (
                        <i className="fas fa-circle-play text-white"></i> // New Play Icon
                    )}
                </button>

                <input
                    type="range"
                    id="play-range"
                    min={startYear}
                    max={endYear}
                    value={year}
                    onChange={handleRangeChange}
                    style={{ width: '89%' }}
                />
            </div>
            <div id="container" style={{ height: '400px' }}>
                {dataset && (
                    <HighchartsReact
                        ref={chartRef}
                        highcharts={Highcharts}
                        options={chartOptions}
                    />
                )}
            </div>
        </div>
    );
};

export default HazardLossChart;

